import {Component, Input} from '@angular/core';

export interface SkeletonColumn {
  width?: string;
  minWidth?: string;
  height?: string;
  circle?: boolean;
}

export const SkeletonBuiltinTypes = ['table', 'card', 'data-table', 'input-row', 'form', 'custom', 'profile-data-table'] as const;
export type SkeletonType = (typeof SkeletonBuiltinTypes)[number] | (string & {});

@Component({
  selector: 'app-skeleton',
  templateUrl: './skeleton.component.html',
  styleUrls: ['./skeleton.component.scss'],
  host: {'[style]': 'hostStyles'}
})
export class SkeletonComponent {
  @Input() set rows(val: number) {
    if (val < 0) { val = 0; console.warn('Skeleton rows clamped to 0'); }
    if (val > 100) { val = 100; console.warn('Skeleton rows clamped to 100'); }
    this._rows = val;
  }
  get rows(): number { return this._rows; }
  private _rows = 5;

  @Input() columns: SkeletonColumn[] = [];
  @Input() type: SkeletonType = 'table';
  @Input() animation: 'shimmer' | 'pulse' | 'wave' = 'shimmer';
  @Input() ariaLabel = 'Loading content';
  @Input() formCols = 2;
  @Input() showTitle = false;
  @Input() titleWidth = '25%';

  // Theming inputs
  @Input() rowHeight: number;
  @Input() borderRadius: string;
  @Input() borderColor: string;
  @Input() circleSize: number;
  @Input() animationSpeed: number;
  @Input() shimmerColors: string[];

  get rowsArr(): null[] {
    if (this._rows === 0) return [];
    return new Array(this._rows);
  }

  get showContent(): boolean {
    return this.type === 'custom' || this._rows > 0;
  }

  get isBuiltinType(): boolean {
    return (SkeletonBuiltinTypes as readonly string[]).includes(this.type);
  }

  get hostStyles(): Partial<CSSStyleDeclaration> {
    const speed = this.animationSpeed != null ? Math.max(0.1, this.animationSpeed) : undefined;
    return {
      '--sk-row-height': this.rowHeight ? `${this.rowHeight}px` : undefined,
      '--sk-border-radius': this.borderRadius,
      '--sk-border-color': this.borderColor,
      '--sk-circle-size': this.circleSize ? `${this.circleSize}px` : undefined,
      '--sk-animation-speed': speed ? `${speed}s` : undefined,
      '--sk-shimmer-bg': this.shimmerColors
        ? `linear-gradient(90deg, ${this.shimmerColors.join(', ')})`
        : undefined,
    } as any;
  }
}
